import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  CalendarClock,
  CalendarDays,
  MessageCircle,
  PanelRightClose,
} from "lucide-react";

import { DeadlinesColumn } from "@/components/overview/DeadlinesColumn";
import { CelebrateCard } from "@/components/overview/CelebrateCard";
import type { TodayPanelData } from "@/context/AnserPanelContext";
import { cn } from "@/lib/utils";

const iconButtonClassName = cn(
  "inline-flex size-8 items-center justify-center rounded-g-sm text-g-ink-400 transition-colors duration-150",
  "hover:bg-g-primary-wash hover:text-g-ink-900",
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-g-ring",
);

/**
 * The header of the home page's panel from the platform sketch: a calendar
 * tile, "Idag" and the date, with the close control. Tokens only.
 */
export function TodayPanelHeader({
  dateLabel,
  onClose,
}: {
  dateLabel: string;
  onClose: () => void;
}) {
  const { t } = useTranslation("dashboard");
  return (
    <div
      className="flex shrink-0 items-center gap-3 px-[22px] pt-6"
      data-testid="today-panel-header"
    >
      <span
        aria-hidden="true"
        className="flex size-[46px] shrink-0 items-center justify-center rounded-[15px] bg-g-warning-tint text-g-warning"
      >
        <CalendarDays className="size-[22px]" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[16px] font-bold leading-tight text-g-ink-900">
          {t("overview.panel.title")}
        </div>
        <div
          className="truncate text-[12px] text-g-ink-500"
          data-testid="anser-context"
        >
          {dateLabel}
        </div>
      </div>
      <button
        type="button"
        className={iconButtonClassName}
        onClick={onClose}
        aria-label={t("overview.panel.close")}
        title={t("overview.panel.close")}
      >
        <PanelRightClose className="size-4" />
      </button>
    </div>
  );
}

/**
 * The body of the Idag panel: what is due (amber until done), "Kommande
 * samtal" (booked 1:1s, plus a red row per member overdue for one - GRA-203,
 * falling back to the ask-Anser empty state with neither) and the
 * "Värt att fira" card. CTAs that ask Anser prefill the composer below.
 */
export function TodayPanelBody({
  data,
  onPick,
}: {
  data: TodayPanelData;
  onPick: (prompt: string) => void;
}) {
  const { t } = useTranslation("dashboard");
  return (
    <div className="flex flex-col gap-5" data-testid="today-panel">
      {data.deadlines.length > 0 ? (
        <section
          aria-label={t("overview.deadlines.title")}
          className="flex flex-col gap-2.5"
        >
          <h3 className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[0.1em] text-g-warning">
            <AlertTriangle className="size-3.5" aria-hidden="true" />
            {t("overview.deadlines.title")}
          </h3>
          <DeadlinesColumn cards={data.deadlines} hideHeading />
        </section>
      ) : null}
      <section
        aria-label={t("overview.talks.heading")}
        className="flex flex-col gap-2.5"
      >
        <h3 className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-g-primary-press">
          {t("overview.talks.heading")}
        </h3>
        {data.talks.length > 0 ? (
          <div className="flex flex-col gap-1.5" data-testid="upcoming-talks">
            {data.talks.map((row) => {
              const danger = row.tone === "danger";
              return (
                <div
                  key={row.id}
                  data-testid={`talk-row-${row.id}`}
                  data-tone={row.tone}
                  className={cn(
                    "flex flex-col gap-1 rounded-g-lg border-[1.5px] p-[12px_14px]",
                    danger
                      ? "border-g-danger-fill bg-g-danger-tint"
                      : "border-g-line-strong bg-g-bg-card",
                  )}
                >
                  <span className="flex items-center gap-2 text-[13.5px] font-semibold text-g-ink-900">
                    {danger ? (
                      <CalendarClock className="size-4 text-g-danger" aria-hidden="true" />
                    ) : (
                      <MessageCircle className="size-4 text-g-ink-400" aria-hidden="true" />
                    )}
                    {row.title}
                  </span>
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className={cn(
                        "text-[12px] font-bold",
                        danger ? "text-g-danger" : "text-g-ink-500",
                      )}
                    >
                      {row.meta}
                    </span>
                    <Link
                      to={row.cta.to}
                      className="text-[12.5px] font-bold text-g-primary underline-offset-4 hover:underline"
                    >
                      {row.cta.label}
                      <ArrowRight className="ml-1 inline size-3.5 align-[-2px]" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div
            className="flex flex-col items-start gap-1.5 rounded-g-lg border-[1.5px] border-dashed border-g-line-strong p-[12px_14px]"
            data-testid="upcoming-talks"
          >
            <span className="flex items-center gap-2 text-[13.5px] font-semibold text-g-ink-900">
              <MessageCircle
                className="size-4 text-g-ink-400"
                aria-hidden="true"
              />
              {t("overview.talks.emptyTitle")}
            </span>
            <button
              type="button"
              onClick={() => onPick(t("overview.talks.askPrompt"))}
              className="text-[12.5px] font-bold text-g-primary underline-offset-4 hover:underline"
            >
              {t("overview.talks.ask")}
              <ArrowRight
                className="ml-1 inline size-3.5 align-[-2px]"
                aria-hidden="true"
              />
            </button>
          </div>
        )}
      </section>
      {data.celebrate.length > 0 ? (
        <CelebrateCard items={data.celebrate} onAsk={onPick} />
      ) : null}
    </div>
  );
}
